import { motion } from "motion/react";
import { Quote, Lightbulb, Rocket, Target } from "lucide-react";
import topLogo from "../assets/icons/top_logo.webp";

export default function AboutPage() {
  const values = [
    {
      title: "Innovation",
      description: "Constantly pushing the boundaries of technology to deliver cutting-edge software solutions.",
      icon: Lightbulb,
      color: "bg-pink-500",
      delay: 0.1,
    },
    {
      title: "Dedication",
      description: "Our sincere commitment to understanding your goals and turning your vision into reality.",
      icon: Target,
      color: "bg-[#4F65F1]",
      delay: 0.2,
    },
    {
      title: "Excellence",
      description: "Delivering high-performance, reliable systems that drive your business forward with speed.",
      icon: Rocket,
      color: "bg-brand-purple",
      delay: 0.3,
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-20 px-6 md:px-12 lg:px-24 bg-transparent overflow-hidden selection:bg-blue-500/30">
      
      {/* 1. Hero Section */}
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-12 mt-8 lg:mt-12">
        <div className="flex-1 flex flex-col items-start justify-center relative z-10 w-full">

          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
            className="font-black text-5xl sm:text-7xl lg:text-[6rem] text-white leading-[1.05] tracking-tight mb-6"
          >
            Who <br />
            We Are.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-lg sm:text-xl text-slate-400 max-w-lg font-medium leading-relaxed"
          >
            We are the creative minds and technical experts behind the digital transformation of modern industries.
          </motion.p>
        </div>

        {/* Right Side Logo Graphic */}
        <div className="flex-1 w-full relative h-[300px] sm:h-[400px] flex items-center justify-center lg:justify-end">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8, x: 50 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100, delay: 0.2 }}
            className="relative z-10"
          >
             <img src={topLogo} alt="Wesitex" className="w-64 sm:w-80 lg:w-[28rem] h-auto object-contain drop-shadow-2xl" />
          </motion.div>
        </div>
      </div>

      {/* 2. The Core Message (Quote) */}
      <div className="max-w-6xl mx-auto mt-32 relative z-20">
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, type: "spring", stiffness: 100 }}
          className="relative bg-slate-950/60 rounded-[3rem] p-10 sm:p-16 lg:p-24 shadow-2xl border border-white/10 backdrop-blur-md flex flex-col items-center text-center group overflow-hidden"
        >
          {/* Large Background Quote Icon */}
          <Quote className="absolute -top-10 -left-10 w-64 h-64 text-brand-purple/5 -rotate-12 group-hover:rotate-0 group-hover:scale-110 transition-transform duration-700 pointer-events-none" />
          
          <div className="relative z-10">
            <Quote className="w-12 h-12 sm:w-16 sm:h-16 text-[#4F65F1] mb-8 mx-auto opacity-80" />
            
            <h2 className="font-orbitron font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-snug tracking-wide mb-8">
              "We are a team sincerely dedicated to <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4F65F1] to-brand-purple">helping your business thrive.</span>"
            </h2>
            
            <div className="flex items-center justify-center gap-4">
              <div className="h-1 w-12 bg-[#4F65F1] rounded-full" />
              <span className="font-bold text-slate-400 tracking-widest uppercase text-sm">The Wesitex Promise</span>
              <div className="h-1 w-12 bg-[#4F65F1] rounded-full" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* 3. Our Values */}
      <div className="max-w-7xl mx-auto mt-32 mb-20">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-black text-4xl sm:text-5xl text-white tracking-tight mb-4">What Drives Us</h2>
          <p className="text-slate-400 font-medium">The core pillars that define our culture and work.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
          {values.map((val, index) => (
            <motion.div
              key={val.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: val.delay, duration: 0.6, type: "spring", stiffness: 120 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="bg-slate-950/60 rounded-[2rem] p-8 sm:p-10 border border-white/10 backdrop-blur-md shadow-2xl flex flex-col items-start cursor-pointer hover:border-brand-purple/40 hover:shadow-[0_0_30px_rgba(139,92,246,0.2)] transition-all"
            >
              <div className={`w-16 h-16 rounded-2xl ${val.color} flex items-center justify-center mb-8 shadow-lg shadow-[${val.color}]/30`}>
                <val.icon className="w-8 h-8 text-white" strokeWidth={2.5} />
              </div>
              <h3 className="font-black text-2xl text-white mb-4">{val.title}</h3>
              <p className="text-slate-400 leading-relaxed font-medium">
                {val.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

    </div>
  );
}
